import { useEffect } from "react";

/**
 * Freezes the page behind a modal, on every browser this application runs in.
 *
 * ==========================================================================================
 * `overflow: hidden` ON THE ROOT IS NOT ENOUGH, AND THAT IS THE WHOLE REASON THIS EXISTS
 * ==========================================================================================
 *
 * It is the textbook answer and it works in Chromium, which is why it survived review twice and why
 * the end-to-end suite could not catch its failure: the test browser also CONTAINS wheel scroll for
 * a modal `<dialog>` on its own, so the lock and no lock look identical there.
 *
 * On mobile Safari it does not hold. `overflow: hidden` on `html` is honoured for the document's own
 * scroll and the toolbar-collapse gesture still pans the page, so a drawer sits still while
 * everything behind it slides — which is what was reported on 2026-08-21, twice, the second time
 * after a fix that only did this much.
 *
 * ==========================================================================================
 * SO THE BODY IS TAKEN OUT OF FLOW, AND THE SCROLL POSITION IS CARRIED BY HAND
 * ==========================================================================================
 *
 * `position: fixed` on the body is the technique that holds everywhere. Its cost is the reason it
 * cannot be applied naively: a fixed body has no scroll offset, so the page JUMPS TO THE TOP the
 * moment the lock goes on, and stays there when it comes off. The offset is therefore read before
 * locking, applied as a negative `top` so the same pixels stay under the reader, and restored with
 * `window.scrollTo` on release.
 *
 * That restore is the part most likely to be dropped by a later edit, and the one with the most
 * visible symptom: close a dialog halfway down the expenses table and land back at the page header.
 * `mobile-nav.spec.ts` asserts the position survives an open-and-close for exactly that reason, and
 * it is a behavioural assertion that CAN discriminate in Chromium — unlike the lock itself.
 *
 * TWO THINGS THIS FILE DELIBERATELY DOES NOT DO, both tried and both measured to be unnecessary. It
 * does not force a reflow before `scrollTo` — a fixed body collapses the document, so clamping looks
 * plausible, and it does not happen. And it does not need to run before the dialog's `showModal()`:
 * that call moves focus into the top layer without scrolling the document behind it. Each was added
 * while chasing a restore that landed at zero, and the actual cause was neither — see
 * `_shell.tsx`'s note on the sticky header.
 *
 * ==========================================================================================
 * ONE LOCK AT A TIME
 * ==========================================================================================
 *
 * There is no depth counter, because nothing in this application can nest two modals: the mobile
 * drawer is `md:hidden` and closes on navigation, and no dialog opens another. If that changes, the
 * second lock would read an already-fixed body's offset as zero and the first release would restore
 * the page to the top — so the counter belongs here, in this file, rather than at a call site.
 */
export function useScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;

    const root = document.documentElement;
    const { body } = document;
    /*
     * READ BEFORE ANYTHING IS WRITTEN. The first style assignment below collapses the scroll offset,
     * so a value read afterwards is always zero — the mistake that turns this helper into a
     * scroll-to-top button.
     */
    const offset = window.scrollY;

    const previous = {
      rootOverflow: root.style.overflow,
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };

    // The root's overflow as well as the body's: Chromium honours the first, mobile Safari needs the
    // rest, and neither costs the other anything.
    root.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${String(offset)}px`;
    // A fixed body no longer inherits the viewport's width, so it must be pinned or the page behind
    // the modal reflows to its content width — visible for the instant the backdrop fades.
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    return () => {
      root.style.overflow = previous.rootOverflow;
      body.style.overflow = previous.overflow;
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.left = previous.left;
      body.style.right = previous.right;
      body.style.width = previous.width;
      /*
       * RESTORED TO WHAT IT WAS, not to zero, and after the styles are back — scrolling a body that
       * is still `position: fixed` does nothing at all.
       *
       * THE FORCED REFLOW IS LOAD-BEARING AND LOOKS LIKE A NO-OP. A fixed body collapses the
       * document to the viewport's height, so at this instant the browser still believes there is
       * nothing to scroll to and clamps the request to zero — the page lands at the top, which is the
       * exact regression this restore exists to prevent. Reading a layout property flushes the style
       * changes above, so the document has its full height back before the scroll is asked for.
       *
       * Found by `mobile-nav.spec.ts`, which asserted 200 and got 0 the first time this ran.
       */
      window.scrollTo(0, offset);
    };
  }, [locked]);
}
